import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, Check, CircleDashed, Minus } from 'lucide-react';
import { cn } from '@utils/cn';
import { JobTask as JobTaskType } from '@root/globalTypes';
import { JobTask } from '../task';
import { getCommitment, JobCommitment, sortByProp } from '../helpers';
import { JobDayTab } from '../days/tab';
import { JobDaysCalendar } from '../days/calendar';
import { getDefaultGroupKey, getGroupCompletion } from './helpers';
import { commitmentMeta, UNSCHEDULED_KEY } from './constants';
import { jobDayTabStyle } from '../days/constants';
import { JobTaskGroupProps } from './types';
import { useScrollFade } from '@hooks/useScrollFade';
import { useDragScroll } from '@hooks/useDragScroll';
import { multischeduleEnabled } from '@utils/consts';
import { useToast } from '@hooks/useToast';
import { Button } from '@components/ui/button';
import { TaskDaysPicker } from '@components/scheduling/taskDaysPicker';
import { getFillDates, latestDay } from '@components/scheduling/taskDaysPicker/helpers';
import { TaskDaysPickerApply } from '@components/scheduling/taskDaysPicker/types';
import { useAddScheduleDays } from '@services/schedules/internal/multischedules/addDays';

export const JobTaskGroup = ({ data, stage }: JobTaskGroupProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'pages.job.days' });
  const { days, unscheduled, total, needsSchedule } = data;
  const [activeKey, setActiveKey] = useState(() => getDefaultGroupKey(days));
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  useScrollFade(scrollRef, `${days.length}-${unscheduled.length}`);
  useDragScroll(scrollRef, `${days.length}-${unscheduled.length}`);

  const activeDay = days.find((el) => el.key === activeKey);
  const activeExists = Boolean(activeDay) || (activeKey === UNSCHEDULED_KEY && unscheduled.length);
  const scheduledCount = total - unscheduled.length;
  const completion = getGroupCompletion(data);
  const firstTask = days[0]?.tasks[0] || unscheduled[0];
  const isSingleTask = !days.length && unscheduled.length === 1;
  const isSingleScheduled = days.length === 1 && days[0].tasks.length === 1 && !unscheduled.length;

  const scrollToTab = (key: string, behavior: ScrollBehavior = 'smooth') => {
    const container = scrollRef.current;
    const tab = tabRefs.current.get(key);
    if (!container || !tab) return;
    container.scrollTo({
      left: tab.offsetLeft - (container.clientWidth - tab.clientWidth) / 2,
      behavior,
    });
  };

  const selectTab = (key: string) => {
    setActiveKey(key);
    scrollToTab(key);
  };

  const setTabRef = (key: string) => (el: HTMLButtonElement | null) => {
    if (el) tabRefs.current.set(key, el);
    else tabRefs.current.delete(key);
  };

  useLayoutEffect(() => {
    scrollToTab(activeKey, 'auto');
  }, []);

  useEffect(() => {
    if (activeExists) return;
    const key = getDefaultGroupKey(days);
    setActiveKey(key);
    scrollToTab(key, 'auto');
  }, [activeExists, days]);

  const dayIndexOffset = (key: string) =>
    days
      .slice(
        0,
        days.findIndex((el) => el.key === key),
      )
      .reduce((sum, el) => sum + el.tasks.length, 0);

  const { toast } = useToast();
  const [pickerOpen, setPickerOpen] = useState(false);
  const takenDays = days.map((day) => day.day);
  const referenceScheduleId = days.at(-1)?.tasks.find((task) => task.schedule?.id)?.schedule?.id;
  const canAddDays = multischeduleEnabled && Boolean(referenceScheduleId);
  const addDays = useAddScheduleDays({
    callback: ({ success }) => {
      if (!success) return;
      setPickerOpen(false);
      toast({ title: t('daysAdded') });
    },
  });
  const applyDays = ({ dates, totalDays }: TaskDaysPickerApply) => {
    if (!referenceScheduleId) return;
    addDays.mutate({ copyFromScheduleId: referenceScheduleId, dates, totalDays });
  };
  const fillNext = () => {
    const taken = new Set(days.map((day) => day.key));
    const dates = getFillDates({
      count: unscheduled.length,
      taken,
      after: latestDay([...taken], new Date(Date.now() - 86400000)),
      skipWeekends: true,
    });
    applyDays({ dates, totalDays: total });
  };

  const renderMeta = () => {
    if (!scheduledCount)
      return (
        <span className="flex items-center gap-1 text-xs text-warning-700">
          <CircleDashed className="h-3.5 w-3.5" />
          {t('notScheduled')}
        </span>
      );

    const scheduledTasks = days.flatMap((day) => day.tasks);
    const count = (level: JobCommitment) =>
      scheduledTasks.filter((task) => getCommitment(task) === level).length;
    const parts: { key: JobCommitment | 'notScheduled'; value: number }[] = [
      ...(['rejected', 'draft', 'waiting'] as JobCommitment[]).map((level) => ({
        key: level,
        value: count(level),
      })),
      { key: 'notScheduled' as const, value: unscheduled.length },
      ...(['confirmed', 'done'] as JobCommitment[]).map((level) => ({
        key: level,
        value: count(level),
      })),
    ].filter(({ value }) => value);
    const top = parts[0]?.key || 'confirmed';
    const { icon: Icon, className } = commitmentMeta[top];

    return (
      <span className={cn('flex items-start gap-1 text-xs', className)}>
        <Icon className="mt-px h-3.5 w-3.5 shrink-0" />
        <span>
          {parts.map(({ key, value }) => t(`commitment.${key}`, { count: value })).join(' · ')}
        </span>
      </span>
    );
  };

  const renderBody = () => {
    if (activeKey === UNSCHEDULED_KEY)
      return (
        <>
          {canAddDays && (
            <div className="col-start-1 col-end-5 flex flex-wrap items-center gap-3 border-t-2 border-neutral-200 bg-warning-50 px-6 py-3 max-[899px]:gap-2 max-[899px]:px-3">
              <p className="min-w-[180px] flex-1 text-sm text-neutral-700">
                <b>{t('needDates', { count: unscheduled.length })}</b>
              </p>
              <Button
                variant="tertiary"
                disabled={addDays.isLoading}
                onClick={fillNext}
                className="max-[899px]:h-11 max-[899px]:flex-1 max-[899px]:justify-center">
                {t('fillNext', { count: unscheduled.length })}
              </Button>
              <Button
                disabled={addDays.isLoading}
                onClick={() => setPickerOpen(true)}
                className="max-[899px]:h-11 max-[899px]:flex-1 max-[899px]:justify-center">
                {t('pickDates')}
              </Button>
            </div>
          )}
          {unscheduled.map((task) => (
            <JobTask key={task.id} data={task} stage={stage} takenDays={takenDays} />
          ))}
        </>
      );
    if (!activeDay) return null;
    const offset = dayIndexOffset(activeDay.key);
    return sortByProp<JobTaskType>([...activeDay.tasks], 'position', 'asc').map((task, i) => (
      <JobTask
        key={task.id}
        data={task}
        date={activeDay.date}
        stage={stage}
        day={{ index: offset + i + 1, total }}
        takenDays={takenDays}
      />
    ));
  };

  return (
    <div
      className={cn(
        'col-start-1 col-end-5 grid overflow-hidden rounded-lg border border-neutral-300 [grid-template-columns:subgrid]',
        isSingleTask ? '[&>*:first-child>*]:border-t-0' : '[&>*:nth-child(2)>*]:border-t-0',
        needsSchedule && 'border-dashed border-warning-300',
        'max-[899px]:flex max-[899px]:flex-col max-[899px]:gap-1.5 max-[899px]:bg-neutral-100',
        isSingleScheduled && 'max-[899px]:gap-0',
      )}>
      {isSingleTask && firstTask ? (
        <JobTask
          data={firstTask}
          stage={stage}
          takenDays={takenDays}
          meta={needsSchedule ? renderMeta() : undefined}
        />
      ) : (
        <>
          <div className="contents max-[899px]:grid max-[899px]:grid-cols-[minmax(0,1fr)_auto_48px] max-[899px]:bg-task max-[899px]:shadow-[inset_0_-1px_0_theme(colors.neutral.200)]">
            <div className="col-start-1 col-end-3 flex min-w-0 items-stretch gap-4 bg-task pl-6 pr-3 shadow-[inset_0_-1px_0_theme(colors.neutral.200)] max-[899px]:contents">
              <div className="flex w-44 shrink-0 flex-col justify-center gap-0.5 py-3 max-[899px]:col-start-1 max-[899px]:row-start-1 max-[899px]:w-auto max-[899px]:min-w-0 max-[899px]:py-2.5 max-[899px]:pl-3">
                <h3 className="text-base font-bold text-neutral-800">{firstTask?.name}</h3>
                {renderMeta()}
              </div>
              {Boolean(days.length) && (
                <div className="flex items-center max-[899px]:col-start-2 max-[899px]:row-start-1 max-[899px]:pl-2">
                  {canAddDays ? (
                    <TaskDaysPicker
                      name={firstTask?.name || ''}
                      days={days}
                      total={total}
                      open={pickerOpen}
                      setOpen={setPickerOpen}
                      onJump={selectTab}
                      onApply={applyDays}
                      applying={addDays.isLoading}
                      trigger={
                        <Button
                          variant="ghost"
                          disabled={false}
                          aria-label={t('addDays', { name: firstTask?.name })}
                          onClick={() => setPickerOpen(true)}
                          className="h-[34px] w-[34px] shrink-0 rounded-lg border border-primary-500 bg-white p-0 text-primary-500 hover:bg-primary-50 hover:text-primary-500 max-[899px]:h-11 max-[899px]:w-11">
                          <CalendarPlus className="h-[18px] w-[18px]" />
                        </Button>
                      }
                    />
                  ) : (
                    <JobDaysCalendar days={days} activeKey={activeKey} onSelect={selectTab} />
                  )}
                </div>
              )}
              {!isSingleScheduled && (
                <div
                  ref={scrollRef}
                  className="min-w-0 flex-1 self-end overflow-x-auto pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden max-[899px]:col-[1/-1] max-[899px]:row-start-2 max-[899px]:px-2 max-[899px]:pt-0.5">
                  <div className="flex w-max gap-1 pr-1">
                    {days.map((day) => (
                      <JobDayTab
                        key={day.key}
                        ref={setTabRef(day.key)}
                        data={day}
                        active={day.key === activeKey}
                        onClick={() => selectTab(day.key)}
                      />
                    ))}
                    {Boolean(unscheduled.length) && (
                      <button
                        ref={setTabRef(UNSCHEDULED_KEY)}
                        type="button"
                        onClick={() => selectTab(UNSCHEDULED_KEY)}
                        aria-pressed={activeKey === UNSCHEDULED_KEY}
                        className={cn(
                          jobDayTabStyle.base,
                          activeKey === UNSCHEDULED_KEY
                            ? jobDayTabStyle.unscheduledActive
                            : jobDayTabStyle.unscheduled,
                        )}>
                        <CircleDashed className="h-3.5 w-3.5" />
                        {t('unscheduled', { count: unscheduled.length })}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="col-start-3 col-end-4 flex justify-end bg-task shadow-[inset_0_-1px_0_theme(colors.neutral.200)] max-[899px]:col-start-3 max-[899px]:col-end-4 max-[899px]:row-start-1 max-[899px]:justify-center max-[899px]:bg-transparent max-[899px]:shadow-none">
              <div className="flex w-fit items-center border-x border-neutral-100 px-4.5 py-4 max-[899px]:border-0 max-[899px]:px-0">
                <span
                  role="img"
                  aria-label={t(`status.${completion === true ? 'completed' : 'scheduled'}`)}
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded border border-neutral-400 bg-white max-[899px]:h-7 max-[899px]:w-7',
                    completion === true && 'border-primary-500 bg-primary-500 text-white',
                    completion === 'indeterminate' &&
                      'border-primary-500 bg-primary-100 text-primary-700',
                  )}>
                  {completion === true && <Check className="h-4 w-4" strokeWidth={3.5} />}
                  {completion === 'indeterminate' && (
                    <Minus className="h-4 w-4" strokeWidth={3.5} />
                  )}
                </span>
              </div>
            </div>
            <div className="col-start-4 col-end-5 bg-task shadow-[inset_0_-1px_0_theme(colors.neutral.200)] max-[899px]:hidden" />
          </div>
          {renderBody()}
        </>
      )}
    </div>
  );
};
